import React, { ReactElement, useContext, useEffect, useRef } from 'react';
import { Field, FieldProps, FormikProps } from 'formik';
import {
    Group,
    Button,
    Input as MaestroInput,
    Text,
    Badge,
    Box,
    Flex,
    Textarea,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import {
    NewPackFormFields,
    NewPackFormKeys,
} from 'pages/NewPack/NewPack.types';
import { AuthenticationContext } from 'contexts/AuthenticationContext';
import { fieldOrder, fieldTitles, fieldDescriptions } from '../../constants';
import { FormFields } from '../../types/fields';
import { StoreContext } from '../../App';
import { i18n } from '../../utils/setupTranslations';
import { FormKeys } from '../../types';
import Select from './Select';
import Radio from './Radio';

type FormFieldComponentFieldProps<F> = FieldProps<F>['field'];

export interface FormFieldComponentProps<F>
    extends FormFieldComponentFieldProps<F> {
    form: FieldProps<F>['form'];
    isInvalid: boolean;
    className?: string;
    placeholder?: string;
}

type FormFieldProps<F> = {
    title: string;
    description?: string;
    placeholder?: string;
    field: FieldProps<F>['field'];
    form: FieldProps<F>['form'];
    component: React.FC<FormFieldComponentProps<F>>;
    descriptionComponent?: ReactElement<any, any> | null;
    isDescriptionOnTop?: boolean;
};

export const FormField = function <F extends FormFields | NewPackFormFields>({
    field,
    form,
    title,
    description,
    placeholder,
    component: Component,
    descriptionComponent,
    isDescriptionOnTop = false,
}: FormFieldProps<F>): JSX.Element {
    const { name } = field;
    const error = name in form.touched ? form.errors[name as keyof F] : '';

    return (
        <Group direction="vertical" gutter="xs">
            {!isDescriptionOnTop && (
                <Group.Item>
                    <Text fontWeight="600">{title}</Text>
                </Group.Item>
            )}
            <Group.Item>
                {isDescriptionOnTop && (
                    <>
                        <Text fontWeight="600">{title}</Text>
                        <Box mb={'xs'}>
                            <Text color="neutral.400" size="md">
                                {description}
                            </Text>
                        </Box>
                    </>
                )}
                <Component
                    {...field}
                    form={form}
                    isInvalid={!!error}
                    className={error && 'isInvalid'}
                    placeholder={placeholder}
                />
                {description && !isDescriptionOnTop && (
                    <Box mt="xxs">
                        <Text color="neutral.400" size="sm">
                            {description}
                        </Text>
                    </Box>
                )}
                {descriptionComponent && descriptionComponent}
            </Group.Item>
            {error && (
                <Box mt="0px">
                    <Badge
                        variant={'danger'}
                        isRounded
                        content={error as string}
                    />
                </Box>
            )}
        </Group>
    );
};

export const Input: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = ({ form, ...rest }) => {
    return (
        <MaestroInput
            variant="filled"
            placeholder={rest.placeholder}
            {...rest}
        />
    );
};

export const TextArea: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = ({ form, ...rest }) => {
    return (
        <Textarea
            placeholder={rest.placeholder}
            height={'120px'}
            variant="ghost"
            backgroundColor={'neutral.800'}
            {...rest}
        />
    );
};

export const LabelField: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = (fieldProps) => {
    const { labels, isLandr } = useContext(AuthenticationContext);
    const { form, ...rest } = fieldProps;

    // Show text input for LANDR
    if (isLandr) {
        return <MaestroInput variant="filled" width="50%" {...rest} />;
    }

    const labelsMapped = labels?.map((label) => ({
        key: label,
        value: label,
    }));

    const options = labelsMapped?.length > 0 ? labelsMapped : undefined;

    return <Select fieldProps={fieldProps} options={options} />;
};

export const ArtistField: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = (fieldProps) => {
    const { artists, isLandr } = useContext(AuthenticationContext);
    const { form, ...rest } = fieldProps;

    // Show text input for LANDR
    if (isLandr) {
        return <MaestroInput {...rest} variant="filled" width="100%" />;
    }

    const artistMapped = artists?.map((artist) => ({
        key: artist,
        value: artist,
    }));

    const options = artistMapped?.length > 0 ? artistMapped : undefined;

    return <Select fieldProps={fieldProps} options={options} />;
};

export const GenreSelect: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = (fieldProps) => {
    const { genres: storeGenres } = useContext(StoreContext);
    return (
        <Select
            fieldProps={fieldProps}
            options={storeGenres.map(({ code }: { code: string }) => ({
                key: code,
                value: i18n._(code),
            }))}
        />
    );
};

export const SubgenreSelect: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = (fieldProps) => {
    const { genres } = useContext(StoreContext);
    const { form } = fieldProps;

    const [currentGenre] =
        genres.filter(
            ({ code }) =>
                code === (form.values as FormFields)[FormKeys.PackGenre] ||
                code ===
                    (form.values as NewPackFormFields)[NewPackFormKeys.Genre],
        ) || [];

    const { subGenres: subgenres } = currentGenre || {};

    return (
        <Select
            fieldProps={fieldProps}
            options={
                subgenres &&
                subgenres.map(({ code }: { code: string }) => ({
                    key: code,
                    value: i18n._(code),
                }))
            }
        />
    );
};

export const ExclusivityRadio: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = () => {
    return (
        <Box mt="0px">
            <Text color="neutral.400" size="md">
                <Trans>
                    Does your pack only contain samples available only on our
                    platform?
                </Trans>
            </Text>

            <Flex flexDirection="column">
                <Box role="group" display="flex" flexDirection="column">
                    <Radio
                        name="exclusivity"
                        value={'true'}
                        label={i18n._(t`Exclusively on LANDR Samples`)}
                    />
                    <Radio
                        name="exclusivity"
                        value={'false'}
                        label={i18n._(t`Available elsewhere`)}
                    />
                </Box>
            </Flex>
        </Box>
    );
};

export const LexiconRadio: React.FC<FormFieldComponentProps<
    FormFields | NewPackFormFields
>> = () => {
    return (
        <Box mt="0px">
            <Text color="neutral.400" size="md">
                <Trans>
                    We have two groups of categories: one for sample packs
                    designated for music production, and the other for SFX
                    intended for post-production work. Please indicate under
                    which category your pack primarily fits best.
                </Trans>
            </Text>

            <Flex flexDirection="column">
                <Box role="group" display="flex" flexDirection="column">
                    <Radio
                        name="useSfxLexicon"
                        value={'false'}
                        label={i18n._(t`Instrument sample pack - Intended for music
                            production (e.g., strings, drums, vocals, EDM
                            risers)`)}
                    />
                    <Radio
                        name="useSfxLexicon"
                        value={'true'}
                        label={i18n._(t`SFX sample pack - Intended for post-production (e.g
                            foley, sound design, etc.)`)}
                    />
                </Box>
            </Flex>
        </Box>
    );
};

const fieldComponents: {
    [key in keyof FormFields]: React.FC<
        FormFieldComponentProps<FormFields | NewPackFormFields>
    >;
} = {
    [FormKeys.PackName]: Input,
    [FormKeys.Description]: Input,
    [FormKeys.LabelName]: Input,
    [FormKeys.PackGenre]: GenreSelect,
    [FormKeys.PackSubgenre]: SubgenreSelect,
    [FormKeys.Exclusivity]: ExclusivityRadio,
    [FormKeys.Lexicon]: LexiconRadio,
};

const Form: React.FC<FormikProps<FormFields>> = ({
    handleSubmit,
    values,
    setValues,
}) => {
    // Remove Subgenre selection when changing Genre
    const genre = useRef<string>(values[FormKeys.PackGenre]);
    useEffect(() => {
        if (genre.current !== values[FormKeys.PackGenre]) {
            setValues({ ...values, [FormKeys.PackSubgenre]: '' });
            genre.current = values[FormKeys.PackGenre];
        }
    }, [setValues, values]);

    return (
        <form onSubmit={handleSubmit}>
            <Group direction="vertical" gutter="xxl">
                {fieldOrder.map((fieldKey: keyof FormFields) => (
                    <Group.Item key={`form-${fieldKey}`}>
                        <Field
                            name={fieldKey}
                            render={(
                                formikProps: FieldProps<FormFields>,
                            ): JSX.Element => (
                                <FormField
                                    {...formikProps}
                                    key={`form-field-${fieldKey}`}
                                    title={fieldTitles[fieldKey]}
                                    description={fieldDescriptions[fieldKey]}
                                    component={fieldComponents[fieldKey]}
                                />
                            )}
                        />
                    </Group.Item>
                ))}
                <Group.Item>
                    <Button type="submit">
                        <Trans>Confirm</Trans>
                    </Button>
                </Group.Item>
            </Group>
        </form>
    );
};

export default Form;
